import React, { memo, useMemo } from 'react';
import { Dialog, DialogActions, DialogContent, LinearProgress } from '@mui/material';
import { IUseForm, useFormHook, Controls, validateRule } from 'AurionCR/components/formV2';
import { DialogBase, useAuth } from 'AurionCR/components';
import { useDeleteDialog } from 'AurionCR/components/form/hooks';
import { DialogHeading } from 'components/dialog-title';
import { useRequestNotify } from 'components/hooks';
import { IGridEditFormProps, IGridFormProps } from 'components/models';

import { usePermissions } from 'hooks/use-permissions';
import { useTranslate } from 'hooks/use-translate';
import { API_USER_PATIENT_PROFILE_NOTES, Notes } from 'services/user-patient-profile-notes';
import { useFieldProps } from 'hooks';
import { Controller } from 'react-hook-form';
import { SelectEmployee } from 'components/select-employee';
import { AppDatePicker } from 'components/app-date-picker';
import { AppInput } from 'components/app-input';

export const Form: React.FC<IGridFormProps<Notes>> = ({ isLoading, control, errors }) => {
  const { t } = useTranslate();
  const getFieldProps = useFieldProps({ errors, emptyHelperText: '' });

  return (
    <>
      <Controller
        control={control}
        name={'userEmployeeProfileID'}
        rules={validateRule('required')}
        render={({ field: renderProps }) => (
          <SelectEmployee
            {...getFieldProps(renderProps)}
            label={t('employee')}
            disableClearable
            disabled={isLoading}
          />
        )}
      />
      <Controller
        control={control}
        name={'entryDate'}
        rules={validateRule('required')}
        render={({ field: renderProps }) => (
          <AppDatePicker {...getFieldProps(renderProps)} label={t('date')} disabled={isLoading} />
        )}
      />
      <Controller
        control={control}
        name={'description'}
        rules={validateRule('required')}
        render={({ field: renderProps }) => (
          <AppInput
            {...getFieldProps(renderProps)}
            disabled={isLoading}
            multiline
            minRows={4}
            maxRows={8}
          />
        )}
      />
    </>
  );
};

interface Props extends IGridEditFormProps {
  userPatientProfileID: string;
}

export default memo<Props>(({ onClose, onFormHandle, itemID, userPatientProfileID }) => {
  const { t } = useTranslate();
  const { user } = useAuth();

  const config = useMemo<IUseForm<Notes>>((): any => {
    return {
      editID: itemID,
      fields: {
        userEmployeeProfileID: user.appUserID || '',
        description: '',
        entryDate: new Date() as any,
      },
      endpoint: API_USER_PATIENT_PROFILE_NOTES.API_ENDPOINT,
      formHandle: onFormHandle,
    };
  }, [itemID, onFormHandle, user.appUserID]);
  const {
    formLoading,
    setFormLoading,
    formTitle,
    formIsNew,
    formUse: {
      control,
      formState: { errors },
      handleSubmit,
    },
    onSubmit,
    deleteItem,
  } = useFormHook(config);
  // request noty wrapper
  const { requestWrap: wrapSubmit } = useRequestNotify({
    request: (data) => {
      return onSubmit({ ...data, userPatientProfileID: userPatientProfileID });
    },
    setLoading: setFormLoading,
  });
  const { requestWrap: wrapDelete } = useRequestNotify({
    request: deleteItem,
    setLoading: setFormLoading,
  });
  // delete helper
  const { handleClose, handleConfirm, handleOpen, isOpenDelete } = useDeleteDialog({
    deleteMiddleware: wrapDelete,
  });
  const isAllowToDelete = usePermissions('isAllowToDeleteAnything');

  return (
    <Dialog open={true} onClose={onClose} fullWidth maxWidth="xs">
      <DialogHeading title={' '} onClose={onClose} />
      {formLoading && <LinearProgress />}
      <DialogContent style={{ position: 'relative' }}>
        <Form isLoading={formLoading} errors={errors} control={control} />
        {isOpenDelete && isAllowToDelete && (
          <DialogBase
            title={formTitle}
            onClose={handleClose}
            onConfirm={handleConfirm}
            isLoading={formLoading}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Controls
          size="medium"
          state={!formIsNew ? 'save' : 'create'}
          loading={formLoading}
          onSubmit={handleSubmit(wrapSubmit)}
          onDelete={handleOpen}
          onCancel={onClose}
          showDelete={!formIsNew && isAllowToDelete}
          CancelButtonProps={{
            children: t('cancel'),
          }}
        />
      </DialogActions>
    </Dialog>
  );
});
